import { useEffect, useMemo, useRef, useState } from "react"
import {
  faCalendarCheck,
  faChartLine,
  faClock,
  faFilter,
  faGear,
  faLocationDot,
  faPeopleGroup,
  faUser,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import List, { type ListItem } from "../../../components/List/List"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useGetCustomer } from "../../../hooks/Costumer/useGetCustomer"
import { useRestaurantReservations } from "../../../hooks/RestaurantReservation/useRestaurantReservations"
import type { CustomerEntity } from "../../../Objects/Customer/CustomerEntity"
import { customerMemory } from "../../../Objects/Memory/Memory"
import type {
  RestaurantReservation,
  RestaurantReservationArea,
  RestaurantReservationSettings,
} from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { ReservationDateFiltersPopup } from "./ReservationDateFiltersPopup"
import { ReservationDetailsPopup } from "./ReservationDetailsPopup"
import { ReservationMetricsPopup } from "./ReservationMetricsPopup"
import { ReservationSavePopup } from "./ReservationSavePopup"
import { ReservationSettingsPopup } from "./ReservationSettingsPopup"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"

export const RestaurantReservationsPage: React.FC = () => {
  const { t } = useTranslation(["restaurantReservations", "list"])

  const api = useRestaurantReservations()

  const { handleGetCostumer } = useGetCustomer()

  const { setNewConfirmation } = useConfirmation()

  const [searchParams] = useSearchParams()

  const [reservations, setReservations] = useState<RestaurantReservation[]>([])

  const [settings, setSettings] = useState<RestaurantReservationSettings | null>(null)

  const [areas, setAreas] = useState<RestaurantReservationArea[]>([])

  const [customers, setCustomers] = useState<CustomerEntity[]>([])

  const [selectedReservation, setSelectedReservation] = useState<RestaurantReservation>()

  const [editingReservation, setEditingReservation] = useState<RestaurantReservation>()

  const [showSave, setShowSave] = useState(false)

  const [isSavingReservation, setIsSavingReservation] = useState(false)

  const [showSettings, setShowSettings] = useState(false)

  const [showDateFilters, setShowDateFilters] = useState(false)

  const [showMetrics, setShowMetrics] = useState(false)

  const [startDate, setStartDate] = useState(new Date().toInputDate())

  const end = new Date()

  end.setDate(end.getDate() + 7)

  const [endDate, setEndDate] = useState(end.toInputDate())

  const reservationsRequestId = useRef(0)

  const loadReservations = async () => {
    if (!startDate || !endDate || startDate > endDate) return
    const requestId = ++reservationsRequestId.current
    const data = await api.getAll(startDate, endDate)
    if (data && requestId === reservationsRequestId.current) setReservations(data)
  }

  const loadManagement = async () => {
    const data = await api.getManagement()
    if (!data) return
    setSettings(data.settings)
    setAreas(data.areas)
  }

  const loadCustomers = () => handleGetCostumer(() => setCustomers([...customerMemory.listData]))

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadManagement().then(() => {
        const quickAction = searchParams.get("quickAction")
        if (quickAction === "reservation-settings") setShowSettings(true)
        if (quickAction === "reservation-create") setShowSave(true)
        if (quickAction === "reservation-filter") setShowDateFilters(true)
        if (quickAction === "reservation-metrics") setShowMetrics(true)
      })
      void loadCustomers()
    }, 0)

    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => void loadReservations(), 250)

    return () => window.clearTimeout(timer)
  }, [startDate, endDate])

  const cards = useMemo<ListItem[]>(
    () =>
      reservations.map((reservation, index) => {
        const confirmed = reservation.status === "Confirmed"

        const startsAt = new Date(reservation.startsAt)

        const localizedDate = startsAt.toLocaleDateString("pt-BR")

        const localizedTime = startsAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })

        return {
          id: reservation.id,
          index,
          title: reservation.customerName,
          linkImage: "",
          isDisable: !confirmed,
          dataFilter: [reservation.customerName, reservation.areaName, localizedDate, localizedTime],
          children: (
            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div
                className={`group flex flex-col gap-4 rounded-2xl border p-4 transition-all duration-300 ${
                  confirmed
                    ? "border-orange-100 bg-gradient-to-r from-orange-50 to-white hover:scale-[1.01] hover:shadow-lg"
                    : "border-slate-200 bg-slate-100 opacity-75"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-1 gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                        confirmed ? "bg-orange-200 text-orange-700" : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-base font-extrabold tracking-tight text-slate-900">
                        {reservation.customerName}
                      </p>

                      <p className="mt-1 flex items-center gap-2 truncate text-sm font-semibold text-slate-600">
                        <FontAwesomeIcon icon={faLocationDot} className="text-orange-500" />
                        {reservation.areaName}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${
                      confirmed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                    }`}
                  >
                    {t(`restaurantReservations:status.${reservation.status}`)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 border-t border-orange-100 pt-3 text-sm font-semibold text-slate-700">
                  <span className="flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-orange-700">
                    <FontAwesomeIcon icon={faCalendarCheck} />
                    {localizedDate}
                  </span>
                  <span className="flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-amber-700">
                    <FontAwesomeIcon icon={faClock} />
                    {localizedTime}
                  </span>
                  <span className="col-span-2 flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-sky-700">
                    <FontAwesomeIcon icon={faPeopleGroup} />
                    {t("restaurantReservations:management.party_size_value", { count: reservation.partySize })}
                  </span>
                </div>
              </div>
            </Animate>
          ),
        }
      }),
    [reservations, t],
  )

  const requestCancellation = (reservation: RestaurantReservation) => {
    setNewConfirmation(t("actions.cancel"), t("management.cancel_confirmation"), async () => {
      if (await api.cancelManagement(reservation.id)) {
        setSelectedReservation(undefined)
        await loadReservations()
      }
    })
  }

  return (
    <>
      <div className={defaultStyle.containerList}>
        <List
          enableLocalPagination
          messageCount={t("management.agenda_count")}
          messageEmpty={t("management.agenda_empty")}
          nameFindSearch={t("management.agenda_search")}
          data={cards}
          nameDropdown={[t("status.Confirmed"), t("status.Cancelled"), t("status.All")]}
          handleAddButton={() => {
            setEditingReservation(undefined)
            setShowSave(true)
          }}
          handleRefreshButton={loadReservations}
          handleExtraFloatButton={() => setShowSettings(true)}
          extraFloatButtonIcon={faGear}
          extraFloatButtonDescriptionKey="interactionDescriptions:restaurantReservations.agenda.settings"
          handleSecondaryExtraFloatButton={() => setShowDateFilters(true)}
          secondaryExtraFloatButtonIcon={faFilter}
          secondaryExtraFloatButtonColor={THEME.colors.primary}
          secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:restaurantReservations.agenda.filters"
          handleImportFloatButton={() => setShowMetrics(true)}
          importFloatButtonIcon={faChartLine}
          importFloatButtonColor="#008268"
          importFloatButtonDescriptionKey="interactionDescriptions:restaurantReservations.agenda.metrics"
          handleOnClick={(id) => setSelectedReservation(reservations.find((item) => item.id === id))}
        />
      </div>

      {selectedReservation && (
        <ReservationDetailsPopup
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(undefined)}
          onEdit={() => {
            setEditingReservation(selectedReservation)
            setSelectedReservation(undefined)
            setShowSave(true)
          }}
          onCancel={() => requestCancellation(selectedReservation)}
        />
      )}

      {showSave && settings && (
        <ReservationSavePopup
          reservation={editingReservation}
          settings={settings}
          areas={areas}
          customers={customers}
          isLoading={isSavingReservation}
          onClose={() => setShowSave(false)}
          onLoadAvailability={api.getManagementAvailability}
          onSave={async (request) => {
            setIsSavingReservation(true)
            try {
              const saved = await api.saveManagementReservation(request)
              if (!saved) return false
              await loadReservations()
              return true
            } finally {
              setIsSavingReservation(false)
            }
          }}
        />
      )}

      {showSettings && settings && (
        <ReservationSettingsPopup
          settings={settings}
          isLoading={api.isLoading}
          onClose={() => setShowSettings(false)}
          onSave={async (value) => {
            const saved = await api.saveSettings(value)
            if (!saved) return false
            setSettings(saved)
            return true
          }}
        />
      )}

      {showDateFilters && (
        <ReservationDateFiltersPopup
          initialFilters={{ startDate, endDate }}
          onClose={() => setShowDateFilters(false)}
          onApplyFilters={(filters) => {
            setStartDate(filters.startDate)
            setEndDate(filters.endDate)
            setShowDateFilters(false)
          }}
        />
      )}

      {showMetrics && (
        <ReservationMetricsPopup
          reservations={reservations}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setShowMetrics(false)}
        />
      )}
    </>
  )
}
